import { useState, useEffect } from "react";
import { Flex, Text, Button, useMantineTheme } from "@mantine/core";
import { Modal as MantineModal } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  EInstrumentType,
  ETypeInstrument,
  IPutPredictiveOrder,
  putPredictiveOrder,
  usePredictiveOrderById,
} from "../../../../hooks/usePredictives";
import InputNumber from "../../../../components/Form/InputNumber";
import Modal from "../../../../components/Modals/ModalResponse";
import { meetingDate } from "../../../../utils/date";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

const predictiveOrderSchema = yup.object().shape({
  quantity: yup
    .number()
    .min(1, "Quantidade é obrigatória")
    .required("Quantidade é obrigatória"),
  price: yup
    .number()
    .min(1, "Preço é obrigatório")
    .required("Preço é obrigatório"),
  id: yup.number().required(),
});
interface IProps {
  setClose: () => void;
  id: number | null;
}

export default function ModalEdit({ id, setClose }: IProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { data: dataPredictive } = usePredictiveOrderById(!!id ? id : 0);
  const predictiveProduct =
    dataPredictive?.data.predictive_instrument?.predictive_product;
  const predictivePercent =
    dataPredictive?.data.predictive_instrument?.percentage || 0;
  const isSideSell = dataPredictive?.data?.order_side === ETypeInstrument.SELL;

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [isErrorModal, setIsErrorModal] = useState(false);

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<IPutPredictiveOrder>({
    resolver: yupResolver(predictiveOrderSchema),
    defaultValues: {
      quantity: dataPredictive?.data?.quantity || 0,
      price: dataPredictive?.data?.price || 0,
      id: id || 0,
    },
  });

  const quantity = Number(watch("quantity")) || 0;
  const price = Number(watch("price")) || 0;

  useEffect(() => {
    if (dataPredictive) {
      setValue("quantity", dataPredictive.data.quantity || 0);
      setValue("price", dataPredictive.data.price || 0);
    }
  }, [dataPredictive]);

  const isDifferentPrice = watch("price") !== dataPredictive?.data?.price;
  const isDifferentQuantity =
    watch("quantity") !== dataPredictive?.data?.quantity;
  const isDifferentPriceAndQuantity = isDifferentPrice || isDifferentQuantity;

  const { mutate } = useMutation({
    mutationFn: async (data: IPutPredictiveOrder) => {
      return await putPredictiveOrder(data);
    },
    onSuccess: () => {
      setIsLoading(false);
      setIsSuccessModal(true);
      queryClient.invalidateQueries({
        queryKey: ["PredictiveOrder"],
        exact: false,
      });
    },
    onError: (error: AxiosError) => {
      setIsLoading(false);
      setIsErrorModal(true);
      console.error("Erro ao editar ordem:", error);
    },
  });

  const onSubmit = (data: IPutPredictiveOrder) => {
    if (!id) return;

    if (quantity <= 0) {
      // Poderia mostrar um erro aqui
      return;
    }

    if (price <= 0) {
      // Poderia mostrar um erro aqui
      return;
    }

    setIsLoading(true);
    mutate({ ...data, id });
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModal(false);
    setClose();
  };

  const handleCloseErrorModal = () => {
    setIsErrorModal(false);
  };

  return (
    <>
      <MantineModal
        opened={!!id}
        onClose={setClose}
        centered
        size="auto"
        transitionProps={{
          transition: "slide-up",
          duration: 300,
          timingFunction: "linear",
          exitDuration: 0,
        }}
        styles={{
          content: {
            borderRadius: "12px",
            boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
          },
          header: {
            padding: "0",
          },
          inner: {
            background: "rgba(0, 0, 0, 0.6)",
          },
          body: {
            padding: "24px",
            maxWidth: "405px",
            color: theme.colors.basic[5],
          },
        }}
        withCloseButton={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex
            direction="column"
            gap="20px"
            p={"0"}
            bg={theme.colors.basic[0]}
            h="fit-content"
            w="100%"
          >
            <Flex
              gap="10px"
              w="100%"
              justify="space-between"
              align="center"
              direction={"column"}
            >
              <Text
                style={{
                  color: theme.colors.basic[5],
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  lineHeight: "140%",
                }}
              >
                {t("operacoes.preditivos.modal_edit.title")}
              </Text>
              <Text
                w={"100%"}
                style={{
                  color: theme.colors.complementary[3],
                  background: theme.colors.complementaryOpacity[3],
                  padding: "0.4rem 0.7rem",
                  borderRadius: "0.5rem",
                  fontSize: "14px",
                  fontWeight: 600,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  flex: "1",
                }}
              >
                {meetingDate({
                  date: predictiveProduct?.expiration || "",
                  category: predictiveProduct?.category || "",
                  t,
                })}
              </Text>
              <Text
                w={"100%"}
                style={{
                  color:
                    predictivePercent > 0
                      ? theme.colors.complementary[0]
                      : predictivePercent === 0
                      ? theme.colors.complementary[7]
                      : theme.colors.complementary[2],
                  background:
                    predictivePercent > 0
                      ? theme.colors.complementaryOpacity[0]
                      : predictivePercent === 0
                      ? theme.colors.complementaryOpacity[7]
                      : theme.colors.complementaryOpacity[2],
                  padding: "0.4rem 0.7rem",
                  borderRadius: "0.5rem",
                  fontSize: "14px",
                  fontWeight: 600,
                  textAlign: "center",
                  flex: "1",
                }}
              >
                {predictivePercent > 0
                  ? t("operacoes.preditivos.modal_edit.contract_increase", {
                      percent: predictivePercent,
                    })
                  : predictivePercent === 0
                  ? t("operacoes.preditivos.modal_edit.contract_maintenance", {
                      percent: predictivePercent,
                    })
                  : t("operacoes.preditivos.modal_edit.contract_remain", {
                      percent: predictivePercent,
                    })}
              </Text>
              <Text
                w={"100%"}
                style={{
                  color: isSideSell
                    ? theme.colors.complementary[4]
                    : theme.colors.complementary[6],
                  background: isSideSell
                    ? theme.colors.complementaryOpacity[4]
                    : theme.colors.complementaryOpacity[6],
                  padding: "0.4rem 0.7rem",
                  borderRadius: "0.5rem",
                  fontSize: "14px",
                  fontWeight: 600,
                  textAlign: "center",
                  flex: "1",
                }}
              >
                {isSideSell
                  ? t("operacoes.preditivos.modal_edit.response_no")
                  : t("operacoes.preditivos.modal_edit.response_yes")}
              </Text>
            </Flex>

            <Text
              style={{
                color: theme.colors.basic[5],
                fontSize: "1rem",
                fontWeight: 600,
                lineHeight: "140%",
              }}
            >
              {predictiveProduct?.title}
            </Text>
            <Flex direction="column" gap="16px" w="100%">
              <InputNumber
                label={t("global.predictives_buy_card.number_contract")}
                placeholder="0"
                min={1}
                max={10000}
                showControls={true}
                value={quantity || ""}
                onChange={(e) => {
                  setValue("quantity", e || 0);
                }}
              />

              <InputNumber
                label={t("global.predictives_buy_card.price")}
                hasIcon
                showControls={true}
                placeholder={t("global.predictives_buy_card.price_range")}
                min={1}
                max={100}
                value={price || ""}
                onChange={(e) => {
                  setValue("price", e || 0);
                }}
              />
            </Flex>

            <Flex gap="12px" w="100%">
              <Button
                variant="outline"
                onClick={setClose}
                style={{ flex: 1 }}
                disabled={isLoading}
              >
                {t("global.button.cancel")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={isLoading}
                disabled={isDirty || !isDifferentPriceAndQuantity}
                style={{ flex: 1 }}
              >
                {t("global.button.save")}
              </Button>
            </Flex>
          </Flex>
        </form>
      </MantineModal>

      <Modal
        type={"success"}
        titleSeccondButton={t("global.modal.close")}
        description={t(
          "operacoes.preditivos.modal_edit.modal_success.description"
        )}
        open={isSuccessModal}
        setClose={handleCloseSuccessModal}
        secondClick={handleCloseSuccessModal}
      />

      <Modal
        type={"danger"}
        description={t(
          "operacoes.preditivos.modal_edit.modal_error.description"
        )}
        open={isErrorModal}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
        titleFirstButton={t("global.modal.close")}
      />
    </>
  );
}
